import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { useRewardTiers } from '@fhs/loyalty';
import {
  IconBottomNavigationRewards,
  IconChevronRight,
  IconLocked,
  IconStarRateFill,
  Text,
  XStack,
  YStack,
  tokens,
} from '@fhs/ui';
import Link from '@fhs-legacy/frontend/src/components/link';
import { useAuthContext } from '@fhs-legacy/frontend/src/state/auth';
import { useLoyaltyUser } from '@fhs-legacy/frontend/src/state/loyalty/hooks/use-loyalty-user';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';

import { PointsRowSkeleton } from './points-row-skeleton';

function getGreeting(hour: number) {
  if (hour < 5) return 'HUNGRY';
  if (hour < 12) return 'GOOD MORNING';
  if (hour < 17) return 'GOOD AFTERNOON';
  return 'GOOD EVENING';
}

function formatCompactPoints(points: number) {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  })
    .format(points)
    .toLowerCase();
}

function getRewardBadgeLabel({
  isPointsLoading,
  hasRedeemableReward,
  pointsToNextReward,
}: {
  isPointsLoading: boolean;
  hasRedeemableReward: boolean;
  pointsToNextReward: number | null;
}) {
  if (isPointsLoading) {
    return null;
  }

  if (hasRedeemableReward) {
    return 'You have rewards available!';
  }

  if (pointsToNextReward) {
    return `${formatCompactPoints(pointsToNextReward)} points away from next reward!`;
  }

  return null;
}

export function AuthenticatedHeader() {
  const { user } = useAuthContext();
  const { loyaltyUser, loading: isLoyaltyUserLoading } = useLoyaltyUser();
  const { data: rewardTiers } = useRewardTiers();
  const firstName = user?.details?.name?.trim().split(/\s+/)[0] || '';
  const greeting = `${getGreeting(new Date().getHours())}${firstName ? `, ${firstName.toUpperCase()}` : ''}!`;
  const isPointsLoading = isLoyaltyUserLoading && !loyaltyUser;
  const points = loyaltyUser?.points ?? 0;
  const minimumRewardPoints = useMemo(() => {
    const validRewardPoints = rewardTiers
      ?.map(tier => tier.points)
      .filter(rewardPoints => Number.isFinite(rewardPoints) && rewardPoints > 0);

    return validRewardPoints?.length ? Math.min(...validRewardPoints) : null;
  }, [rewardTiers]);
  const hasRedeemableReward = minimumRewardPoints !== null && points >= minimumRewardPoints;
  const pointsToNextReward =
    minimumRewardPoints === null ? null : Math.max(minimumRewardPoints - points, 0);
  const rewardBadgeLabel = getRewardBadgeLabel({
    isPointsLoading,
    hasRedeemableReward,
    pointsToNextReward,
  });
  const rewardsState = rewardBadgeLabel ? `, ${rewardBadgeLabel}` : '';

  return (
    <YStack style={styles.container}>
      <Text weight="semibold" style={styles.greeting} accessibilityRole="header">
        {greeting}
      </Text>
      {isPointsLoading ? (
        <PointsRowSkeleton />
      ) : (
        <Link
          to={routes.rewards}
          accessibilityRole="button"
          accessibilityLabel={`${points.toLocaleString()} points${rewardsState}`}
          style={styles.pointsRow}
        >
          <View style={styles.pointsIcon}>
            <IconStarRateFill size={22} color="#E0A52E" aria-hidden />
          </View>
          <Text style={styles.pointsValue}>{points.toLocaleString()}</Text>
          <Text.Ui size="sm">points</Text.Ui>
          <IconChevronRight size={16} color={tokens.colors.$houseDark} aria-hidden />
        </Link>
      )}
      {rewardBadgeLabel ? (
        <XStack style={styles.rewardBadge}>
          {hasRedeemableReward ? (
            <IconBottomNavigationRewards size={12} color={tokens.colors.$houseDark} aria-hidden />
          ) : (
            <IconLocked size={12} color={tokens.colors.$houseDark} aria-hidden />
          )}
          <Text weight="semibold" style={styles.rewardBadgeText}>
            {rewardBadgeLabel}
          </Text>
        </XStack>
      ) : null}
    </YStack>
  );
}

const styles = StyleSheet.create({
  container: { alignItems: 'center', gap: 12 },
  greeting: { fontSize: 16, lineHeight: 22, textAlign: 'center' },
  pointsRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    minHeight: 44,
  },
  pointsIcon: {
    alignItems: 'center',
    backgroundColor: tokens.colors.$blackOpacity04,
    borderRadius: 100,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  pointsValue: { fontFamily: 'HeroSerifBold', fontSize: 36, lineHeight: 40 },
  rewardBadge: {
    alignItems: 'center',
    backgroundColor: 'rgba(237, 191, 98, 0.1)',
    borderColor: '#E0A52E',
    borderRadius: 4,
    borderWidth: 1,
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 5,
  },
  rewardBadgeText: { fontSize: 12, lineHeight: 17 },
});
